/* eslint-disable @atlaskit/platform/use-recommended-utils */
import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ProductKeys3P,
	type ProductKeys3P as ProductKeys3PType,
} from '@atlassian/rovo-connectors-config/types';
import { type ExperimentConfig } from '@atlassian/search-experiment/types';

import {
	FEDERATED_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../constants/schemas/3p-config';

export const serveConvoStartersSameLine: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('kd_convo_starters_same_line', 'cohort', 'control') ===
		'test'
	);
};

export const removeFedSlackFromAllProductSearch = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isAllProductSearch: boolean,
): boolean | undefined => {
	const isSlackAuthenticated = !thirdPartyConfigs?.[ProductKeys3P.Slack]?.userNeedsOAuth;
	const hasFederatedConnectorSource = thirdPartyConfigs?.[
		ProductKeys3P.Slack
	]?.connectorSources?.includes(FEDERATED_CONNECTOR_SOURCE);
	const isUserEligible = isSlackAuthenticated && hasFederatedConnectorSource && isAllProductSearch;

	if (!isUserEligible) {
		return isUserEligible;
	}

	// The latency experiment keeps federated Slack in the all-products request path so
	// we can measure request behavior without the FPS-side exclusion. The gate scopes who
	// is enrolled at all, and the experiment splits enrolled users into cohorts.
	const isLatencyExperimentEnabled =
		fg('ai_3p_slack_search_latency_experiment_enabled') &&
		FeatureGates.getExperimentValue<boolean>('ai-3p-latency-slack-experiment', 'isEnabled', false, {
			fireExperimentExposure: false,
		});

	return !isLatencyExperimentEnabled;
};

export const serveNounResultCounts: (fireExperimentExposure?: boolean) => boolean = (
	fireExperimentExposure = false,
): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('rovosearch_noun_result_counts', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};

export const serveWebPagesInTypeFilter: (fireExperimentExposure?: boolean) => boolean = (
	fireExperimentExposure = false,
): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('web_pages_type_filter_bug', 'cohort', 'control', {
			fireExperimentExposure,
		}) === 'test'
	);
};

// A centralised util to check if the search_user_right_rail_3p_personalization_v1 is enabled and logging an exposure.
export const serveRightRailRecommendationsImprovementsWithExposure: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'search_user_right_rail_3p_personalization_v1',
			'cohort',
			'control',
			{
				fireExperimentExposure: true,
			},
		) === 'test'
	);
};

export const serveQF3PBannerExperiment: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('quick-find-3p-banner', 'bannerType', 'control') !==
		'control'
	);
};

export const serveQF3PBannerExperimentWithoutExposure: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>('quick-find-3p-banner', 'bannerType', 'control', {
			fireExperimentExposure: false,
		}) !== 'control'
	);
};

export const serveConfluenceSpaceIconUpdate: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>('confluence_space_icon_update', 'isEnabled', false) ===
		true
	);
};

// Experiment: https://hello.atlassian.net/wiki/x/okE3bgE
export const serve3PClickIngestionExperiment: (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	options?: GetExperimentValueOptions<string>,
) => boolean = (
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: true,
	},
): boolean => {
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return (
		isEligible3PUser &&
		FeatureGates.getExperimentValue<string>(
			'3p_clicks_in_fps_as_qf_recents',
			'cohort',
			'control',
			options,
		) === 'test'
	);
};

/**
 * A centralised util to check if the Quick Find 3P boosting experiment is enabled.
 * This experiment prioritizes 3rd party documents in the recent activities section.
 * Note: This is a standalone experiment (not mutually exclusive with other Quick Find experiments).
 */
export const serveQuickFind3PBoostingExperiment: (options?: {
	fireExperimentExposure?: boolean;
}) => boolean = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_boosting_experiment',
			'cohort',
			'control',
			{
				fireExperimentExposure,
			},
		) === 'test'
	);
};

// Experiment: https://switcheroo.atlassian.com/ui/gates/ca4413ca-ac7e-4b41-a744-cd86b29d783c/key/afm_customer_contact_search_type_experiment
export const serveCustomerContactEntityType: () => boolean = (): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'afm_customer_contact_search_type_experiment',
			'cohort',
			'control',
		) === 'test'
	);
};

export const getMultiSiteSearchInRovo: (experimentConfig: ExperimentConfig) => boolean = (
	experimentConfig: ExperimentConfig,
): boolean => {
	const cohort = experimentConfig.frontendExperiments?.['multi_site_search_in_rovo']?.cohort;

	return cohort === 'test';
};

/**
 * Resolves whether the multi-site search experiment is enabled for the current user.
 * Only served for Confluence and Jira
 * */

const IMPROVE_BANNER_TARGETING_COHORTS = ['control', 'usage-threshold', 'max-dismissals'] as const;
export type ImproveBannerTargetingCohort = (typeof IMPROVE_BANNER_TARGETING_COHORTS)[number];

/**
 * A centralised util to check the improve-banner-targeting experiment cohort.
 *
 * Cohort 1 ('usage-threshold'): Enforce a minimum product usage threshold before showing the OAuth banner.
 * Cohort 2 ('max-dismissals'): Apply isHello-specific dismissal logic (cross-product max dismissals) for all users.
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/0cfc0b4b-2fad-46e6-9d6f-247dbfdbee02/key/rovo_search_improve_banner_targeting
 */
export const serveImproveBannerTargetingExperiment = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): ImproveBannerTargetingCohort => {
	const cohort = FeatureGates.getExperimentValue<string>(
		'rovo_search_improve_banner_targeting',
		'cohort',
		'control',
		{ fireExperimentExposure },
	);

	if (IMPROVE_BANNER_TARGETING_COHORTS.includes(cohort as ImproveBannerTargetingCohort)) {
		return cohort as ImproveBannerTargetingCohort;
	}

	return 'control';
};

/**
 * Shared eligibility check for the FPS GitHub auth banner surfaces
 * (currently the `fps_github_social_proof_banner` experiment and the
 * `fps_github_auth_banner_promo` feature gate).
 *
 * A user is eligible when, on the GitHub 3P config:
 *  1. The GitHub connector is enabled in the tenant.
 *  2. The user has not yet connected to GitHub.
 *
 * This helper does NOT fire any Statsig exposure — it is a pure eligibility
 * gate intended to be evaluated BEFORE consulting an experiment value or
 * feature gate, so that ineligible users never enter the Statsig flow.
 */
export const isEligibleForFPSGitHubAuthBannerSurfaces = ({
	thirdPartyConfigs,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined;
}): boolean => {
	const githubConfig = thirdPartyConfigs?.[ProductKeys3P.Github];
	if (!githubConfig) {
		return false;
	}
	// 1. GitHub connector enabled in tenant.
	if (typeof githubConfig.isEnabled === 'function' && !githubConfig.isEnabled()) {
		return false;
	}
	// 2. User is not yet connected to GitHub.
	if (githubConfig.userNeedsOAuth !== true) {
		return false;
	}
	return true;
};

/**
 * Cohorts for the `fps_github_social_proof_banner` experiment.
 *  - `control`                     — the compact one-line banner ("Your admin
 *                                    has added {product}!" + "Connect your
 *                                    account to see missing results").
 *  - `full_social_proof_banner`    — the large social-proof banner (GIF +
 *                                    dynamic social-proof title).
 *  - `inline_social_proof_banner`  — the compact one-line banner, but with the
 *                                    dynamic social-proof title ("Your team
 *                                    connected {product}" / "{percentage}% of
 *                                    your team has connected apps like
 *                                    {product}") and the same "Connect your
 *                                    account to see missing results" description.
 */
const FPS_GITHUB_SOCIAL_PROOF_BANNER_COHORTS = [
	'control',
	'full_social_proof_banner',
	'inline_social_proof_banner',
] as const;
export type FPSGitHubSocialProofBannerCohort =
	(typeof FPS_GITHUB_SOCIAL_PROOF_BANNER_COHORTS)[number];

/**
 * Returns the FPS GitHub social proof banner experiment cohort for the user.
 *
 * Eligibility is enforced as an exposure gate — ineligible users always return
 * `control` and the Statsig experiment value is never evaluated (no exposure is
 * fired).
 *
 * A user is eligible when, on the GitHub 3P config:
 *  - The connector is enabled in the tenant.
 *  - The user has not yet connected to GitHub.
 *
 * Switcheroo: https://switcheroo.atlassian.com/ui/gates/4563e9ea-649e-4541-a6bb-09163274a5ef/key/fps_github_social_proof_banner
 */
export const getFPSGitHubSocialProofBannerCohort = (
	{
		thirdPartyConfigs,
	}: {
		thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined;
	},
	options: GetExperimentValueOptions<string> = { fireExperimentExposure: false },
): FPSGitHubSocialProofBannerCohort => {
	if (!isEligibleForFPSGitHubAuthBannerSurfaces({ thirdPartyConfigs })) {
		return 'control';
	}
	const cohort = FeatureGates.getExperimentValue<string>(
		'fps_github_social_proof_banner',
		'cohort',
		'control',
		options,
	);

	if (FPS_GITHUB_SOCIAL_PROOF_BANNER_COHORTS.includes(cohort as FPSGitHubSocialProofBannerCohort)) {
		return cohort as FPSGitHubSocialProofBannerCohort;
	}

	return 'control';
};

/**
 * Returns whether the user is in a cohort that renders a social-proof title for
 * the FPS GitHub banner — i.e. any cohort other than `control`
 * (`full_social_proof_banner` or `inline_social_proof_banner`). This gates the
 * team-connected percentage trait fetch and the experiment exposure. To pick the
 * specific banner variant to render, use `getFPSGitHubSocialProofBannerCohort`.
 *
 * Eligibility is enforced as an exposure gate — ineligible users always return
 * `false` and the Statsig experiment value is never evaluated (no exposure is fired).
 */
export const serveFPSGitHubSocialProofBannerExperiment = (
	{
		thirdPartyConfigs,
	}: {
		thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined;
	},
	options: GetExperimentValueOptions<string> = { fireExperimentExposure: false },
): boolean => getFPSGitHubSocialProofBannerCohort({ thirdPartyConfigs }, options) !== 'control';

/**
 * When the user is eligible, the Statsig feature gate `fps_github_auth_banner_promo` is consulted to
 * decide whether the promo behaviour should be applied
 * 	- GitHub-only banner
 * 	- snooze fatigue management
 * 	- bypass legacy gates (i.e, max dismissal count, usage threshold)
 */
export const serveFPSGitHubAuthBannerPromo = ({
	thirdPartyConfigs,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap | undefined;
}): boolean => {
	if (!isEligibleForFPSGitHubAuthBannerSurfaces({ thirdPartyConfigs })) {
		return false;
	}
	return fg('fps_github_auth_banner_promo');
};

/**
 * A centralised util to get the experiment value for the unified search right rail enhancements experiment.
 * This experiment separates connected products from unconnected products in the right panel and involves 3 cohorts/variations:
 * 1. control - UI before experiment with app filters combined
 * 2. single-select - shows the connected and unconnected products in separate sections with single-select app filters
 * 3. multi-select - shows the connected and unconnected products in separate sections with multi-select app filters
 * Experiment: https://switcheroo.atlassian.com/ui/gates/2db9f352-e361-4337-9d3e-395a68a87d57/key/unified_search_right_rail_enhancements
 */
export const getUnifiedSearchRightRailEnhancementsExpValue = (): string => {
	return FeatureGates.getExperimentValue<string>(
		'unified_search_right_rail_enhancements',
		'cohort',
		'control',
	);
};

/**
 * Experiment for surfacing Slack top reactions in search results.
 * 1. cohort: control -> existing experience without Slack top reactions
 * 2. cohort: non-control -> enable Slack top reactions in search results
 * Experiment: https://switcheroo.atlassian.com/ui/gates/8240d410-8d14-453f-93da-cc98d281b347/key/rovo_fps_show_slack_top_reaction_in_search_ab
 */
export const serveSlackTopReactionInSearchExperiment = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean =>
	FeatureGates.getExperimentValue(
		'rovo_fps_show_slack_top_reaction_in_search_ab',
		'cohort',
		'control',
		{ fireExperimentExposure },
	) !== 'control';

/**
 * Experiment with surfacing 3p docs (google docs and microsoft sharepoint) in recommendations section of QF, and improved 1p docs relevance.
 * 1. isEnabled: true -> surface 3p docs in recs and improved relevance for 1p docs
 * 2. isEnabled: false -> control experience
 * Experiment: https://switcheroo.atlassian.com/ui/gates/d3d5d238-7efa-4eaa-a83d-bc2ac8698633/key/quick_find_zero_query_3p_recs_experiment
 */
export const serveQFZeroQuery3PRecsExperiment = (
	options: GetExperimentValueOptions<boolean> = {
		fireExperimentExposure: false,
	},
): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'quick_find_zero_query_3p_recs_experiment',
			'isEnabled',
			false,
			options,
		) === true
	);
};

export const QUICK_FIND_RECS_DEFAULT_FEATURE_SERVICE_MODEL = 'quickfind_user_recs';

/**
 * Test cohort `featureServiceModel` for Quick Find's 3P recommendation-model
 * experiments.
 */
export const QUICK_FIND_RECS_CHALLENGER_FEATURE_SERVICE_MODEL = 'quickfind_user_recs_challenger_1';

/**
 * Returns whether the current user is eligible for Quick Find's 3P
 * recommendation-model experiments based on their connected third-party
 * connectors.
 *
 * Eligibility: user has at least one authorised Google Drive OR SharePoint
 * connector (top-level or per-datasource OAuth completed).
 */
export const isEligibleForQFRecs3PExperiment = (
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): boolean => {
	if (!thirdPartyConfigs) {
		return false;
	}

	const isAuthorised = (productKey: ProductKeys3PType): boolean => {
		const config = thirdPartyConfigs[productKey];
		if (!config) {
			return false;
		}
		if (config.userNeedsOAuth === false) {
			return true;
		}
		return config.datasources?.some((ds) => ds?.userNeedsOAuth === false) ?? false;
	};

	return isAuthorised(ProductKeys3P.GoogleDrive) || isAuthorised(ProductKeys3P.Sharepoint);
};

/**
 * Returns the recommendation model selected by
 * `quick_find_recs_3p_sequence_model_exp` for an eligible user.
 *
 * The default model is the control. The challenger model is delivered only to
 * the experiment test cohort.
 *
 * **Does NOT fire exposure by default.** Callers should pass
 * `{ fireExperimentExposure: true }` at the exact point in the flow where the
 * treatment would actually be delivered (e.g. inside the recommended-documents
 * fetch callback, after all delivery-side guards such as
 * `serveZeroQuery3PRecs`, primary-product, and presence of `cloudId` /
 * `user.id` have passed). This keeps the analysis population aligned with the
 * users who actually receive the treatment. `FeatureGates.getExperimentValue`
 * dedupes repeated exposures for the same user/experiment within a session, so
 * calling this on every fetch is safe.
 *
 * Connector eligibility (Google Drive or SharePoint authorised) is enforced
 * here so we don't pollute the experiment with users who would never see a
 * difference even if the rest of the delivery path were satisfied.
 */
export const getQuickFindRecs3PSequenceModel = (
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	options: GetExperimentValueOptions<string> = { fireExperimentExposure: false },
): string | undefined => {
	if (!isEligibleForQFRecs3PExperiment(thirdPartyConfigs)) {
		return undefined;
	}

	const value = FeatureGates.getExperimentValue<string>(
		'quick_find_recs_3p_sequence_model_exp',
		'featureServiceModel',
		QUICK_FIND_RECS_DEFAULT_FEATURE_SERVICE_MODEL,
		options,
	);

	return value === QUICK_FIND_RECS_CHALLENGER_FEATURE_SERVICE_MODEL
		? QUICK_FIND_RECS_CHALLENGER_FEATURE_SERVICE_MODEL
		: QUICK_FIND_RECS_DEFAULT_FEATURE_SERVICE_MODEL;
};

/**
 * A centralised util to check if the GitHub issues recents experiment is enabled.
 *
 * Pass `fireExperimentExposure: false` when checking for allocation (e.g. building the query),
 * and `fireExperimentExposure: true` when the user is actually exposed to the experience
 * (i.e. when the Quick Find dialog mounts and the user has GitHub connector access).
 *
 * Experiment: https://switcheroo.atlassian.com/ui/gates/f32bf9f8-82fb-4960-95c3-0ecbb2ec604c/key/rovo_search_github_issues_in_recents
 */
export const serveGithubWorkItemsRecentsExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo_search_github_issues_in_recents',
			'cohort',
			'control',
			{
				fireExperimentExposure,
			},
		) === 'test'
	);
};

export const serveSlClickedOnlyInRecentsExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<string>(
			'rovo_search_use_sl_clicked_only_in_recents',
			'cohort',
			'control',
			{ fireExperimentExposure },
		) === 'test'
	);
};

/**
 * Cohorts for QF GitHub tip banner experiment.
 *
 * - `control`            : banner is not rendered at all.
 * - `test-with-gnin`     : banner triggers on EITHER a GitHub keyword OR a
 *                         Jira GNIN-routing intent (typed JQL / work-item
 *                         key). Matches the experiment doc's "Test 1".
 * - `test-keyword-only`  : banner triggers on a GitHub keyword ONLY \u2014 the
 *                         Jira GNIN intent path is skipped. Matches the
 *                         experiment doc's "Test 2", Jira-specific.
 */
const QF_GITHUB_TIP_BANNER_COHORTS = ['control', 'test-with-gnin', 'test-keyword-only'] as const;
export type QfGithubTipBannerCohort = (typeof QF_GITHUB_TIP_BANNER_COHORTS)[number];

/**
 * Resolve QF GitHub tip banner cohort. Pass
 * `fireExperimentExposure: true` only at the eligibility-check site (when
 * QF opens and the user is eligible) so cohort allocation is balanced
 * before the user has had a chance to interact with the banner. Render-time
 * reads should leave `fireExperimentExposure` as the default `false`.
 *
 * Unknown cohort values fall back to `control` so we never accidentally
 * render the banner for users who were allocated to an invalid bucket.
 */
export const serveQfGithubTipBannerExperiment = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): QfGithubTipBannerCohort => {
	const value = FeatureGates.getExperimentValue<string>(
		'rovo_qf_github_tip_banner',
		'cohort',
		'control',
		{ fireExperimentExposure },
	);
	return (QF_GITHUB_TIP_BANNER_COHORTS as readonly string[]).includes(value)
		? (value as QfGithubTipBannerCohort)
		: 'control';
};

const SEARCH_PAGE_PROACTIVE_AI_MULTI_VARIANT_COHORTS = [
	'control',
	'auto_open_chat_modal',
	'on_demand_sain_banner',
	'inline_chat_input_in_results',
	'floating_chat_input',
] as const;

export type SearchPageProactiveAiMultiVariantCohort =
	(typeof SEARCH_PAGE_PROACTIVE_AI_MULTI_VARIANT_COHORTS)[number];

/**
 * Returns the cohort for the `search_page_proactive_ai_multi_variant`
 * experiment.
 *
 * Exposure point: landing on full-page search with a non-empty query,
 * regardless of whether SAIN renders an answer (other variant legs may be
 * eligible when SAIN exists, so exposure must NOT be gated on SAIN). Call
 * with `fireExperimentExposure: true` exactly once at that landing point,
 * and with `fireExperimentExposure: false` everywhere else that only needs
 * to read the assigned cohort.
 * https://console.statsig.com/experiments/search_page_proactive_ai_multi_variant
 */
export const getSearchPageProactiveAiMultiVariantCohort = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): SearchPageProactiveAiMultiVariantCohort => {
	const cohort = FeatureGates.getExperimentValue<string>(
		'search_page_proactive_ai_multi_variant',
		'cohort',
		'control',
		{ fireExperimentExposure },
	);

	if (
		SEARCH_PAGE_PROACTIVE_AI_MULTI_VARIANT_COHORTS.includes(
			cohort as SearchPageProactiveAiMultiVariantCohort,
		)
	) {
		return cohort as SearchPageProactiveAiMultiVariantCohort;
	}

	return 'control';
};

/**
 * Experiment for appending deactivated (inactive/closed) Confluence author accounts to the
 * Contributor filter's option list.
 *
 * Deactivated users are removed from URS, so the Contributor type-ahead never returns them. In the
 * test cohort they are mined from the rendered search results and surfaced in the filter (labelled
 * "(Deactivated)"), and the display fields of such client-only options are preserved through the
 * view -> store boundary so the affordance survives selection.
 *
 * 1. isEnabled: false -> control, the Contributor filter only shows what URS returns
 * 2. isEnabled: true  -> test, mined deactivated users are decorated + appended
 *
 * **Does NOT fire exposure by default.** The cohort value guards the author fields in the search
 * query, so plumbing reads must not emit exposure. Exposure is fired when a Confluence user opens
 * the Contributor dropdown. It cannot depend on mined users because author data is intentionally
 * unavailable in control; doing so would make exposure asymmetric and bias the experiment.
 *
 * Experiment: https://console.statsig.com/experiments/append_deactivated_account_to_contributor_list
 */
export const serveAppendDeactivatedAccountToContributorList = ({
	fireExperimentExposure = false,
}: {
	fireExperimentExposure?: boolean;
} = {}): boolean => {
	return (
		FeatureGates.getExperimentValue<boolean>(
			'append_deactivated_account_to_contributor_list',
			'isEnabled',
			false,
			{ fireExperimentExposure },
		) === true
	);
};

const JIRA_NAVIGATION_TO_GNIN_COHORTS = ['control', 'work-item-icon', 'header-link'] as const;
export type JiraNavigationToGNINCohort = (typeof JIRA_NAVIGATION_TO_GNIN_COHORTS)[number];

/**
 * QS-10204 — Interim experiment that adds a more discoverable FPS → GNIN
 * navigation affordance in the Jira Full Page Search header:
 *   - `control`         → no change (header RovoButton with default TableIcon)
 *   - `work-item-icon`  → swap the header RovoButton's TableIcon for the Jira work-item icon (query echo dropped)
 *   - `header-link`     → render a "View all work items" link next to the Search results heading (query echo dropped)
 *
 * Returns `'control'` when not enrolled / value invalid. Fires a Statsig
 * exposure on every call — only call at the allocation point (i.e. after the
 * `SearchPageHeader` `'button'` cohort guard) so the exposure denominator
 * equals the eligible population. The *display* decision is made in
 * `SearchPageHeader`; this helper only resolves which interim cohort the user
 * is in.
 */
export const serveJiraNavigationToGNIN = ({
	fireExperimentExposure = true,
}: {
	fireExperimentExposure?: boolean;
} = {}): JiraNavigationToGNINCohort => {
	const cohort = FeatureGates.getExperimentValue<string>(
		'rovo_jira_navigation_to_gnin',
		'cohort',
		'control',
		{ fireExperimentExposure },
	);

	if (JIRA_NAVIGATION_TO_GNIN_COHORTS.includes(cohort as JiraNavigationToGNINCohort)) {
		return cohort as JiraNavigationToGNINCohort;
	}

	return 'control';
};
